import { useEffect, useState, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/models";
import { SkillType } from "@/enums";
import moment from "moment";
import { getSkillsMetadata } from "@/services/weeklyPlanner/query/getSkillsMetadata";
import { getStudentSkillsMastery } from "@/services/weeklyPlanner/query/getStudentSkillsMastery";
import { getTeacherAssignments } from "@/services/weeklyPlanner/query/getTeacherAssignments";
import { getCurriculum } from "@/services/weeklyPlanner/query/getCurriculum";
import { aggregateAssignmentsToLessons } from "@/utilities/aggregateAssignmentsToLessons";
import { GradeNumericToString } from "@/enums";

export function useRolloverData() {
  const roster = useSelector((state: RootState) => state.reports?.roster);
  const studentsRoster = roster?.data?.student || [];
  const classroomId = roster?.ui?.selections?.classroom?.id;

  const [isLoading, setIsLoading] = useState(false);
  const [prerequisiteSkills, setPrerequisiteSkills] = useState<any[]>([]);
  const [redKids, setRedKids] = useState<any[]>([]);
  const [nextWeekCurriculumData, setNextWeekCurriculumData] = useState<any>(null);

  const hasFetchedCurriculum = useRef(false);
  const hasFetchedPrerequisites = useRef(false);
  const hasFetchedMastery = useRef(false);

  const students = useMemo(() => {
    return studentsRoster
      .filter((s: any) => s.classroom_id.includes(`${classroomId}`))
      .map((s: any) => ({
        ...s,
        firstName: s.first_name,
        lastName: s.last_name,
      }));
  }, [studentsRoster, classroomId]);

  const { nextWeekStart, nextWeekEnd } = useMemo(() => {
    return {
      nextWeekStart: moment().add(1, "week").startOf("week").add(-23, "days"),
      nextWeekEnd: moment().add(1, "week").startOf("week").add(43, "days"),
    };
  }, []);

  const nextWeekFocusSkills = useMemo(() => {
    const skills: any[] = [];
    if (nextWeekCurriculumData?.tracks) {
      nextWeekCurriculumData.tracks.forEach((track: any) => {
        track.units?.forEach((unit: any) => {
          unit.lessons?.forEach((lesson: any) => {
            const lessonDate = moment(lesson.dateFrom);
            if (lessonDate.isBetween(nextWeekStart, nextWeekEnd, "day", "[]")) {
              lesson.skills
                .filter((skill: any) => skill.skillType === SkillType.FOCUS)
                .forEach((skill: any) => {
                  skills.push({
                    ...skill,
                    lessonName: lesson.lessonName || lesson.name,
                    lessonDate: lesson.dateFrom,
                    unitName: unit.name,
                    trackName: track.name,
                  });
                });
            }
          });
        });
      });
    }
    return skills;
  }, [nextWeekCurriculumData, nextWeekStart, nextWeekEnd]);

  useEffect(() => {
    hasFetchedCurriculum.current = false;
    hasFetchedPrerequisites.current = false;
    hasFetchedMastery.current = false;
    setNextWeekCurriculumData(null);
    setPrerequisiteSkills([]);
    setRedKids([]);
  }, [classroomId]);

  useEffect(() => {
    if (!classroomId || hasFetchedCurriculum.current) return;

    const fetchNextWeekCurriculum = async () => {
      setIsLoading(true);
      try {
        const assignments = await getTeacherAssignments(
          classroomId,
          nextWeekStart.format("YYYY-MM-DD"),
          nextWeekEnd.format("YYYY-MM-DD"),
          GradeNumericToString[studentsRoster[0]?.grade]
        );

        const lessonPlanIds = [
          ...new Set(assignments?.assignment?.map((a: any) => a.lessonPlanId).filter(Boolean)),
        ];

        if (lessonPlanIds.length > 0) {
          const curriculumResponse = await getCurriculum(lessonPlanIds);
          const aggregated = aggregateAssignmentsToLessons({
            assignments: assignments.assignment,
            curriculumInfo: curriculumResponse.getCurriculumInfoForLessons,
            dateFrom: nextWeekStart.format("YYYY-MM-DD"),
            dateTo: nextWeekEnd.format("YYYY-MM-DD"),
            stories: {},
            studentCount: students.length,
          });
          setNextWeekCurriculumData(aggregated);
        }
      } catch (error) {
        console.error("Error fetching next week curriculum:", error);
      } finally {
        hasFetchedCurriculum.current = true;
        setIsLoading(false);
      }
    };

    fetchNextWeekCurriculum();
  }, [classroomId, nextWeekStart, nextWeekEnd, students.length, studentsRoster]);

  useEffect(() => {
    if (nextWeekFocusSkills.length === 0 || hasFetchedPrerequisites.current) return;

    const fetchPrerequisites = async () => {
      setIsLoading(true);
      try {
        const skillIds = [...new Set(nextWeekFocusSkills.map((s) => s.skillId).filter(Boolean))];
        const { skillsMetadata } = await getSkillsMetadata(skillIds);
        const prereqIds = [...new Set(skillsMetadata.flatMap((m) => m.preReqSkills))];
        if (!prereqIds.length) return;

        const { skillsMetadata: prereqMetadata } = await getSkillsMetadata(prereqIds);

        const enhanced = nextWeekFocusSkills.map((skill) => {
          const meta = skillsMetadata.find((m) => m.skillId === skill.skillId);
          const prereqs =
            meta?.preReqSkills.map((pid) => {
              const pMeta = prereqMetadata.find((p) => p.skillId === pid);
              return {
                skillId: pid,
                timeToMaster: pMeta?.timeToMaster,
              };
            }) || [];

          return {
            ...skill,
            prerequisites: prereqs,
            timeToMaster: meta?.timeToMaster,
          };
        });

        setPrerequisiteSkills(enhanced);
      } catch (error) {
        console.error("Error fetching prerequisites:", error);
      } finally {
        hasFetchedPrerequisites.current = true;
        setIsLoading(false);
      }
    };

    fetchPrerequisites();
  }, [nextWeekFocusSkills]);

  useEffect(() => {
    if (
      prerequisiteSkills.length === 0 ||
      students.length === 0 ||
      hasFetchedMastery.current
    )
      return;

    const fetchMasteryData = async () => {
      setIsLoading(true);
      try {
        const prereqIds = [
          ...new Set(
            prerequisiteSkills.flatMap((s) => s.prerequisites?.map((p: { skillId: string }) => p.skillId) || [])
          ),
        ];
        if (!prereqIds.length) return;

        const { studentSkillsMastery } = await getStudentSkillsMastery({
          studentId: students.map((s: { id: string }) => s.id),
          skillId: prereqIds,
          dateFrom: moment().format("YYYY-MM-DD"),
          dateTo: moment().format("YYYY-MM-DD"),
        });

        const redStudents = prerequisiteSkills.flatMap((focusSkill) =>
          (focusSkill.prerequisites || []).flatMap((prereq: any) =>
            students.flatMap((student: any) => {
              const studentMastery = studentSkillsMastery.find(
                (m: any) => m.studentId === student.id
              );
              const prereqRecord = studentMastery?.records.find(
                (r: any) => r.skillId === prereq.skillId
              );
              return prereqRecord?.pacing === "not_developed"
                ? [{ student, focusSkill, missingPrerequisite: prereq }]
                : [];
            })
          )
        );

        const unique = redStudents.filter(
          (item, idx, arr) =>
            idx ===
            arr.findIndex(
              (other) =>
                other.student.id === item.student.id &&
                other.focusSkill.skillId === item.focusSkill.skillId
            )
        );

        setRedKids(unique);
      } catch (error) {
        console.error("Error fetching mastery data:", error);
        setRedKids([]);
      } finally {
        hasFetchedMastery.current = true;
        setIsLoading(false);
      }
    };

    fetchMasteryData();
  }, [prerequisiteSkills, students]);

  return {
    isLoading,
    nextWeekFocusSkills,
    prerequisiteSkills,
    redKids,
    students,
    nextWeekStart,
    nextWeekEnd,
  };
}

export default useRolloverData;
